import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { AlertTriangle, Clock, Calendar, Fuel, ChevronDown, ArrowRight } from 'lucide-react';
import VehicleLayoutWrapper from '../components/VehicleLayoutWrapper';
import HeaderBar from '../components/HeaderBar';

const VehicleDashboard = () => {
  const [ownHireStatus, setOwnHireStatus] = useState('OWN');
  const [fuelConsumption, setFuelConsumption] = useState('Low');

  // Sample data based on your screenshot
  const renewalAlerts = {
    expired: [
      { type: 'Vehicle Insurance', count: 80, status: 'Expired' },
      { type: 'Fitness Certificate', count: 77, status: 'Expired' },
      { type: 'Road Tax', count: 74, status: 'Expired' },
      { type: 'National Permit', count: 74, status: 'Expired' }
    ],
    expireToday: [
      { type: 'Vehicle Insurance', count: 0, status: 'Expire Today' },
      { type: 'Fitness Certificate', count: 0, status: 'Expire Today' },
      { type: 'Road Tax', count: 0, status: 'Expire Today' },
      { type: 'National Permit', count: 0, status: 'Expire Today' }
    ],
    expireSoon: [
      { type: 'Vehicle Insurance', count: 0, status: 'Expire Soon' },
      { type: 'Fitness Certificate', count: 0, status: 'Expire Soon' },
      { type: 'Road Tax', count: 0, status: 'Expire Soon' },
      { type: 'National Permit', count: 0, status: 'Expire Soon' }
    ]
  };

  const serviceAlerts = {
    expired: [
      { id: '1303', location: 'DECEMBER', details: 'TEST KM LSA' },
      { id: '1168', location: 'UNKNOWN', details: 'P965' },
      { id: '1366', location: '', details: 'P795' },
      { id: 'ICE ALERT TEST', location: 'TEST KM LSA', details: '' },
      { id: 'MACH 001', location: '', details: '' }
    ],
    expireNearby: [
      { id: '1303', location: 'DECEMBER', details: 'TEST KM LSA' },
      { id: 'UNKNOWN', location: '', details: 'P965' },
      { id: '', location: '', details: 'P795' }
    ]
  };

  const vehicleData = [
    { name: 'Branch B HILARA', ownVehicle: 100, value: 100 },
    { name: 'HO SUB 4 SLT AIZWAL', ownVehicle: 25, value: 25 },
    { name: 'sub6 TUMTHAKWN', ownVehicle: 5, value: 5 },
    { name: 'ZAWNGDN', ownVehicle: 2, value: 2 }
  ];

  // Navigation function for dashboard reports
  const navigateToDashboard = () => {

   window.open('/vehicle/DashboardReport1','_blank');
  };

  const AlertCard = ({ title, count, type, status, onClick }) => (
    <div 
      className="relative bg-white rounded-full border-4 border-red-200 w-32 h-32 flex flex-col items-center justify-center cursor-pointer hover:border-red-300 hover:shadow-lg transition-all duration-200 group"
      onClick={onClick}
    >
      {/* Expired stamp effect */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-red-400 text-6xl font-bold opacity-20 transform rotate-12">
          EXPIRED
        </div>
      </div>
      
      <div className="relative z-10 text-center">
        <div className="text-2xl font-bold text-red-600 mb-1">{count}</div>
        <div className="text-xs font-medium text-gray-700 text-center leading-tight px-2">
          {title}
        </div>
        <div className="text-xs text-red-500 font-medium mt-1">{status}</div>
      </div>
      
      {/* Hover effect */}
      <div className="absolute inset-0 bg-red-50 rounded-full opacity-0 group-hover:opacity-20 transition-opacity duration-200"></div>
    </div>
  );

  const ServiceAlertCard = ({ title, items, onClick }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h4 className="font-semibold text-gray-800 mb-3 text-sm">{title}</h4>
      <div className="space-y-2">
        {items.map((item, index) => (
          <div 
            key={index}
            className="p-2 bg-gray-50 rounded text-xs cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={() => onClick(title, items)}
          >
            <div className="text-gray-700">{item.id}</div>
            <div className="text-gray-500">{item.location}</div>
            <div className="text-gray-500">{item.details}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <VehicleLayoutWrapper>
        <HeaderBar title='Vehicle and Fleet Dashboard' />
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* RENEWAL ALERTS Section */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="bg-red-500 text-white px-4 py-2 rounded-t-lg mb-6 -mx-6 -mt-6">
            <h2 className="font-bold text-lg flex items-center">
              <AlertTriangle className="mr-2 h-5 w-5" />
              RENEWAL ALERTS
            </h2>
          </div>

          {/* Expired Section */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <div className="bg-red-100 text-red-800 px-3 py-1 rounded-lg text-sm font-semibold">
                Expired
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 justify-items-center">
              {renewalAlerts.expired.map((alert, index) => (
                <AlertCard
                  key={index}
                  title={alert.type}
                  count={alert.count}
                  status={alert.status}
                  onClick={ navigateToDashboard}
                />
              ))}
            </div>
          </div>

          {/* Expire Today Section */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <div className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-lg text-sm font-semibold">
                Expire Today
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 justify-items-center">
              {renewalAlerts.expireToday.map((alert, index) => (
                <AlertCard
                  key={index}
                  title={alert.type}
                  count={alert.count}
                  status={alert.status}
                  onClick={() => navigateToDashboard()}
                />
              ))}
            </div>
          </div>

          {/* Expire Soon Section */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <div className="bg-orange-100 text-orange-800 px-3 py-1 rounded-lg text-sm font-semibold">
                Expire Soon
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 justify-items-center">
              {renewalAlerts.expireSoon.map((alert, index) => (
                <AlertCard
                  key={index}
                  title={alert.type}
                  count={alert.count}
                  status={alert.status}
                  onClick={() => navigateToDashboard()}
                />
              ))}
            </div>
          </div>
        </div>

        {/* SERVICE EXPIRY ALERTS Section */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="bg-red-500 text-white px-4 py-2 rounded-t-lg mb-6 -mx-6 -mt-6">
            <h2 className="font-bold text-lg flex items-center">
              <Clock className="mr-2 h-5 w-5" />
              SERVICE EXPIRY ALERTS
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <ServiceAlertCard
              title="Service Expired"
              items={serviceAlerts.expired}
              onClick={(type, items) => navigateToDashboard()}
            />
            <ServiceAlertCard
              title="Expire Nearly"
              items={serviceAlerts.expireNearby}
              onClick={(type, items) => navigateToDashboard()}
            />
          </div>
        </div>

        {/* Bottom Section with Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* OWN/HIRE Vehicle Status */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <label className="font-semibold text-gray-700">OWN / HIRE VEHICLE STATUS</label>
              <select 
                value={ownHireStatus}
                onChange={(e) => setOwnHireStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="OWN">OWN</option>
                <option value="HIRE">HIRE</option>
              </select>
            </div>
            
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={vehicleData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  fontSize={10}
                />
                <YAxis />
                <Tooltip />
                <Bar dataKey="ownVehicle" fill="#3B82F6" name="OWN VEHICLE" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Fuel Price Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <label className="font-semibold text-gray-700">FUEL PRICE</label>
            </div>
            <div className="flex items-center justify-center h-48 bg-gray-100 rounded-lg">
              <div className="text-center">
                <div className="text-red-600 bg-red-100 px-4 py-2 rounded-lg text-sm mb-4">
                  No fuel consumption found for this input date range !
                </div>
                <div className="text-gray-500 text-sm">Chart unavailable</div>
              </div>
            </div>
          </div>

          {/* Fuel Consumption Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <label className="font-semibold text-gray-700">FUEL CONSUMPTION</label>
              <select 
                value={fuelConsumption}
                onChange={(e) => setFuelConsumption(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
            <div className="flex items-center justify-center h-48 bg-gray-100 rounded-lg">
              <div className="text-center">
                <div className="text-red-600 bg-red-100 px-4 py-2 rounded-lg text-sm mb-4">
                  Data unavailable for this type filling
                </div>
                <div className="text-gray-500 text-sm">Chart unavailable</div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Navigation</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <button
              onClick={() => navigateToDashboard('expired', renewalAlerts.expired)}
              className="flex items-center justify-center px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              <span className="mr-2">Expired</span>
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => navigateToDashboard('expire-today', renewalAlerts.expireToday)}
              className="flex items-center justify-center px-4 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
            >
              <span className="mr-2">Expire Today</span>
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => navigateToDashboard('expire-soon', renewalAlerts.expireSoon)}
              className="flex items-center justify-center px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              <span className="mr-2">Expire Soon</span>
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => navigateToDashboard('service-expired', serviceAlerts.expired)}
              className="flex items-center justify-center px-4 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
            >
              <span className="mr-2">Service Expired</span>
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => navigateToDashboard('expire-nearly', serviceAlerts.expireNearby)}
              className="flex items-center justify-center px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <span className="mr-2">Expire Nearly</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleDashboard;